// src/services/branchService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  orderBy,
  query,
  where,
  serverTimestamp,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import {
  Warehouse,
  WarehouseFormData,
  WarehouseAddress,
  WarehouseContact,
  WarehouseCapacity,
  WarehouseOperatingHours,
  WarehouseDashboardStats,
  WarehouseWithInventory,
  WarehouseInventory,
  DAYS_OF_WEEK,
  createDefaultDayHours,
} from '../../../enums/product';
import { WarehouseInventoryService } from '../car/BranchInventoryService';

const WAREHOUSES_COLLECTION = 'warehouses';

// ── Graceful degradation for pre-existing documents ──────────────────────────
// Older warehouse docs may still have the flat `{ open, close, timezone }`
// shape (before operating hours became per-day). Rather than crashing or
// silently mis-typing that data as the new shape, detect it and surface it as
// "not set" (undefined) so the UI can show its own per-day defaults instead
// of stale/garbled values. Docs already in the new per-day shape pass through
// with any missing day filled in with sensible defaults.
function normalizeOperatingHours(raw: any): WarehouseOperatingHours | undefined {
  if (!raw) return undefined;

  const hasPerDayData = DAYS_OF_WEEK.some(day => raw[day] && typeof raw[day] === 'object');
  if (!hasPerDayData) return undefined; // old flat shape (or empty) — treat as not set

  const normalized = { timezone: raw.timezone || 'UTC' } as WarehouseOperatingHours;
  DAYS_OF_WEEK.forEach(day => {
    const dayData = raw[day];
    (normalized as any)[day] = dayData
      ? {
          open: dayData.open || '09:00',
          close: dayData.close || '18:00',
          closed: !!dayData.closed,
        }
      : createDefaultDayHours();
  });
  return normalized;
}

export interface PaginatedWarehousesResult {
  warehouses: Warehouse[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

interface GroupedProduct {
  productId: string;
  hasVariants: boolean;
  totalQuantity: number;
  isOutOfStock: boolean;
  isLowStock: boolean;
  variants: WarehouseInventory[];
}

export class WarehouseService {

  /* ===================== GET WAREHOUSES PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getWarehousesPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: boolean
  ): Promise<PaginatedWarehousesResult> {
    try {
      const warehouses: Warehouse[] = [];
      const collectionRef = collection(db, WAREHOUSES_COLLECTION);

      // ── Build filter constraints (where clauses only, no ordering/paging) ──
      const filterConstraints: any[] = [];
      if (statusFilter !== undefined) {
        filterConstraints.push(where('isActive', '==', statusFilter));
      }

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const warehouse: Warehouse = {
          id: docSnap.id,
          name: data.name || '',
          code: data.code || '',
          description: data.description || '',
          address: data.address as WarehouseAddress || { street: '', city: '', state: '', country: '', zipCode: '' },
          contact: data.contact as WarehouseContact || { phone: '', email: '', manager: '' },
          capacity: data.capacity as WarehouseCapacity || { total: 0, used: 0, available: 0 },
          isActive: data.isActive !== false,
          operatingHours: normalizeOperatingHours(data.operatingHours),
          displayOnWebsite: data.displayOnWebsite === true,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          createdBy: data.createdBy,
          createdByName: data.createdByName,
          updatedBy: data.updatedBy,
          updatedByName: data.updatedByName,
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const s = searchQuery.toLowerCase();
          const match =
            warehouse.name.toLowerCase().includes(s) ||
            warehouse.code.toLowerCase().includes(s) ||
            warehouse.description.toLowerCase().includes(s) ||
            warehouse.address.street.toLowerCase().includes(s) ||
            warehouse.address.city.toLowerCase().includes(s) ||
            warehouse.address.state.toLowerCase().includes(s) ||
            warehouse.address.country.toLowerCase().includes(s) ||
            warehouse.address.zipCode.toLowerCase().includes(s) ||
            warehouse.contact.manager.toLowerCase().includes(s) ||
            warehouse.contact.phone.toLowerCase().includes(s) ||
            warehouse.contact.email.toLowerCase().includes(s);
          if (match) warehouses.push(warehouse);
        } else {
          warehouses.push(warehouse);
        }
      });

      return {
        warehouses: await this.withRealCapacity(warehouses),
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching paginated warehouses:', error);
      throw error;
    }
  }

  static async createWarehouse(formData: WarehouseFormData, userId: string, userName: string): Promise<string> {
    try {
      const existingWarehouse = await this.getWarehouseByCode(formData.code);
      if (existingWarehouse) throw new Error('Show Room code already exists');

      const totalCapacity = parseFloat(formData.capacity.total) || 0;
      const usedCapacity = parseFloat(formData.capacity.used) || 0;

      const capacity: WarehouseCapacity = {
        total: totalCapacity,
        used: usedCapacity,
        available: totalCapacity - usedCapacity,
      };

      const warehouseData = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        description: formData.description,
        address: formData.address,
        contact: formData.contact,
        capacity,
        isActive: formData.isActive,
        operatingHours: formData.operatingHours,
        displayOnWebsite: formData.displayOnWebsite === true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        createdByName: userName,
      };

      const docRef = await addDoc(collection(db, WAREHOUSES_COLLECTION), warehouseData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating warehouse:', error);
      throw error;
    }
  }

  private static groupInventoryProducts(inventory: WarehouseInventory[]): GroupedProduct[] {
    const grouped = new Map<string, GroupedProduct>();

    inventory.forEach(item => {
      if (!grouped.has(item.productId)) {
        grouped.set(item.productId, {
          productId: item.productId,
          hasVariants: !!item.variantId,
          totalQuantity: 0,
          isOutOfStock: false,
          isLowStock: false,
          variants: [],
        });
      }

      const group = grouped.get(item.productId)!;
      if (item.variantId) {
        group.variants.push(item);
        group.totalQuantity += item.quantity;
      } else {
        group.totalQuantity = item.quantity;
        group.isOutOfStock = item.isOutOfStock;
        group.isLowStock = item.isLowStock;
      }
    });

    grouped.forEach(group => {
      if (group.hasVariants && group.variants.length > 0) {
        group.isOutOfStock = group.variants.every(v => v.isOutOfStock);
        group.isLowStock = !group.isOutOfStock && group.variants.some(v => v.isLowStock);
      }
    });

    return Array.from(grouped.values());
  }

  static async getWarehouseWithInventory(warehouseId: string): Promise<WarehouseWithInventory | null> {
    try {
      const warehouse = await this.getWarehouse(warehouseId);
      if (!warehouse) return null;

      const inventory = await WarehouseInventoryService.getWarehouseInventory(warehouseId);
      const groupedProducts = this.groupInventoryProducts(inventory);
      const totalStock = inventory.reduce((sum, item) => sum + item.quantity, 0);
      const lowStockCount = groupedProducts.filter(p => p.isLowStock && !p.isOutOfStock).length;
      const outOfStockCount = groupedProducts.filter(p => p.isOutOfStock).length;

      return {
        ...warehouse,
        inventory,
        stats: {
          productCount: groupedProducts.length,
          variantCount: inventory.filter(i => i.variantId).length,
          totalStock,
          lowStockCount,
          outOfStockCount,
        },
      };
    } catch (error) {
      console.error('Error fetching warehouse with inventory:', error);
      throw error;
    }
  }

  static async getActiveWarehousesWithCapacity(): Promise<Warehouse[]> {
    try {
      const q = query(
        collection(db, WAREHOUSES_COLLECTION),
        where('isActive', '==', true),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => this.mapDocToWarehouse(doc));
    } catch (error) {
      console.error('Error fetching active warehouses:', error);
      throw error;
    }
  }

  static async getWarehouseUtilizationStats(warehouseId: string): Promise<{
    utilizationRate: number;
    capacityUsed: number;
    capacityAvailable: number;
    capacityTotal: number;
  }> {
    try {
      const warehouse = await this.getWarehouse(warehouseId);
      if (!warehouse) throw new Error('Show Room not found');

      const utilizationRate = Math.round((warehouse.capacity.used / warehouse.capacity.total) * 100);
      return {
        utilizationRate,
        capacityUsed: warehouse.capacity.used,
        capacityAvailable: warehouse.capacity.available,
        capacityTotal: warehouse.capacity.total,
      };
    } catch (error) {
      console.error('Error getting warehouse utilization stats:', error);
      throw error;
    }
  }

  static async getDashboardStats(): Promise<WarehouseDashboardStats> {
    try {
      const warehouses = await this.getWarehouses();
      const total = warehouses.length;
      const active = warehouses.filter(w => w.isActive).length;
      const inactive = total - active;
      const totalCapacity = warehouses.reduce((sum, w) => sum + w.capacity.total, 0);
      const usedCapacity = warehouses.reduce((sum, w) => sum + w.capacity.used, 0);
      const availableCapacity = totalCapacity - usedCapacity;
      const utilizationRate = totalCapacity > 0 ? Math.round((usedCapacity / totalCapacity) * 100) : 0;

      return { total, active, inactive, totalCapacity, usedCapacity, availableCapacity, utilizationRate };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      throw error;
    }
  }

  static async updateWarehouseCapacity(
    warehouseId: string,
    capacity: { total?: number; used?: number; available?: number }
  ): Promise<void> {
    try {
      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      const updateData: any = { updatedAt: serverTimestamp() };

      if (capacity.total !== undefined) updateData['capacity.total'] = capacity.total;
      if (capacity.used !== undefined) updateData['capacity.used'] = capacity.used;
      if (capacity.available !== undefined) updateData['capacity.available'] = capacity.available;

      await updateDoc(warehouseRef, updateData);
    } catch (error) {
      console.error('Error updating warehouse capacity:', error);
      throw error;
    }
  }

  static async getWarehousesWithLowCapacity(threshold: number = 20): Promise<Warehouse[]> {
    try {
      const warehouses = await this.getActiveWarehouses();
      return warehouses.filter(warehouse => {
        const availablePercentage = (warehouse.capacity.available / warehouse.capacity.total) * 100;
        return availablePercentage < threshold;
      });
    } catch (error) {
      console.error('Error getting warehouses with low capacity:', error);
      throw error;
    }
  }

  static async updateWarehouse(warehouseId: string, formData: WarehouseFormData, userId: string, userName: string): Promise<void> {
    try {
      const existingWarehouse = await this.getWarehouse(warehouseId);
      if (!existingWarehouse) throw new Error('Show Room not found');

      const warehouseWithSameCode = await this.getWarehouseByCode(formData.code);
      if (warehouseWithSameCode && warehouseWithSameCode.id !== warehouseId) {
        throw new Error('Show Room code already exists');
      }

      const totalCapacity = parseFloat(formData.capacity.total) || 0;
      const usedCapacity = parseFloat(formData.capacity.used) || 0;
      const capacity: WarehouseCapacity = {
        total: totalCapacity,
        used: usedCapacity,
        available: totalCapacity - usedCapacity,
      };

      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      await updateDoc(warehouseRef, {
        name: formData.name,
        code: formData.code.toUpperCase(),
        description: formData.description,
        address: formData.address,
        contact: formData.contact,
        capacity,
        isActive: formData.isActive,
        operatingHours: formData.operatingHours,
        displayOnWebsite: formData.displayOnWebsite === true,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
        updatedByName: userName,
      });
    } catch (error) {
      console.error('Error updating warehouse:', error);
      throw error;
    }
  }

  static async deleteWarehouse(warehouseId: string): Promise<void> {
    try {
      const hasInventory = await this.checkWarehouseInventory(warehouseId);
      if (hasInventory) {
        throw new Error('Cannot delete show room with existing inventory. Please transfer or remove inventory first.');
      }
      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      await deleteDoc(warehouseRef);
    } catch (error) {
      console.error('Error deleting warehouse:', error);
      throw error;
    }
  }

  /**
   * @deprecated Use getWarehousesPaginated instead
   */
  static async getWarehouses(): Promise<Warehouse[]> {
    try {
      const q = query(collection(db, WAREHOUSES_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => this.mapDocToWarehouse(doc));
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      throw error;
    }
  }

  static async getActiveWarehouses(): Promise<Warehouse[]> {
    try {
      const q = query(
        collection(db, WAREHOUSES_COLLECTION),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => this.mapDocToWarehouse(doc));
    } catch (error) {
      console.error('Error fetching active warehouses:', error);
      throw error;
    }
  }

  static async getWarehouse(warehouseId: string): Promise<Warehouse | null> {
    try {
      const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
      const warehouseSnap = await getDoc(warehouseRef);
      if (warehouseSnap.exists()) return this.mapDocToWarehouse(warehouseSnap);
      return null;
    } catch (error) {
      console.error('Error fetching warehouse:', error);
      throw error;
    }
  }

  static async getWarehouseByCode(code: string): Promise<Warehouse | null> {
    try {
      const q = query(
        collection(db, WAREHOUSES_COLLECTION),
        where('code', '==', code.toUpperCase())
      );
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) return null;
      return this.mapDocToWarehouse(querySnapshot.docs[0]);
    } catch (error) {
      console.error('Error fetching warehouse by code:', error);
      throw error;
    }
  }

  /* ===================== GET WAREHOUSE STATS =====================
   * Uses getCountFromServer() for total/active/inactive counts (no doc reads).
   * Capacity aggregates require document data — fetched in the same
   * Promise.all so the whole stats call is one round trip.
   * ============================================================== */
  static async getWarehouseStats(): Promise<WarehouseDashboardStats> {
    try {
      const col = collection(db, WAREHOUSES_COLLECTION);

      const [totalSnap, activeSnap, docsSnap, occupiedByBranch] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('isActive', '==', true))),
        getDocs(query(col)),
        this.getOccupiedCapacityByBranch(),
      ]);

      const total = totalSnap.data().count;
      const active = activeSnap.data().count;
      const inactive = total - active;

      let totalCapacity = 0;
      let usedCapacity = 0;
      docsSnap.forEach(d => {
        const cap = d.data().capacity;
        totalCapacity += cap?.total || 0;
        usedCapacity += occupiedByBranch[d.id] || 0;
      });

      const availableCapacity = totalCapacity - usedCapacity;
      const utilizationRate = totalCapacity > 0
        ? Math.round((usedCapacity / totalCapacity) * 100)
        : 0;

      return { total, active, inactive, totalCapacity, usedCapacity, availableCapacity, utilizationRate };
    } catch (error) {
      console.error('Error getting warehouse stats:', error);
      throw error;
    }
  }

  static async searchWarehouses(searchTerm: string): Promise<Warehouse[]> {
    try {
      const warehouses = await this.getWarehouses();
      const lower = searchTerm.toLowerCase();
      return warehouses.filter(w =>
        w.name.toLowerCase().includes(lower) ||
        w.code.toLowerCase().includes(lower) ||
        w.description.toLowerCase().includes(lower) ||
        w.contact.manager.toLowerCase().includes(lower) ||
        w.address.city.toLowerCase().includes(lower) ||
        w.address.state.toLowerCase().includes(lower) ||
        w.address.country.toLowerCase().includes(lower)
      );
    } catch (error) {
      console.error('Error searching warehouses:', error);
      throw error;
    }
  }

  // private static async checkWarehouseInventory(warehouseId: string): Promise<boolean> {
  //   const { ProductService } = await import('../car/carService');
  //   try {
  //     const productsInWarehouse = await ProductService.getProductsByWarehouse(warehouseId);
  //     return productsInWarehouse.length > 0;
  //   } catch (error) {
  //     console.error('Error checking warehouse inventory:', error);
  //     return false;
  //   }
  // }

  private static async checkWarehouseInventory(warehouseId: string): Promise<boolean> {
  try {
    const warehouseRef = doc(db, WAREHOUSES_COLLECTION, warehouseId);
    const warehouseSnap = await getDoc(warehouseRef);
    if (!warehouseSnap.exists()) return false;
    const data = warehouseSnap.data();
    const used = data?.capacity?.used ?? 0;
    const totalStock = data?.stats?.totalStock ?? 0;
    return used > 0 || totalStock > 0;
  } catch (error) {
    console.error('Error checking warehouse inventory:', error);
    return false;
  }
}

  static async getWarehouseUtilization(warehouseId: string): Promise<{
    totalProducts: number;
    inStock: number;
    reserved: number;
    sold: number;
  }> {
    try {
      const { ProductService } = await import('../car/carService');
      const products = await ProductService.getProductsByBranch(warehouseId);
      return {
        totalProducts: products.length,
        inStock: products.filter(p => p.status === 'In Stock').length,
        reserved: products.filter(p => p.status === 'Reserved').length,
        sold: products.filter(p => p.status === 'Sold').length,
      };
    } catch (error) {
      console.error('Error getting warehouse utilization:', error);
      throw error;
    }
  }

  // A car occupies physical showroom space while it's 'In Stock', 'Reserved',
  // or 'Sold' (awaiting delivery) at that branch — 'Delivered' cars have left,
  // and 'In Transit (Import)' cars haven't arrived yet.
  private static readonly OCCUPYING_STATUSES = new Set(['In Stock', 'Reserved', 'Sold']);

  // `capacity.used` on the warehouse document is a manually-typed number that
  // nothing keeps in sync with real inventory (no code path ever calls
  // updateWarehouseCapacity()), so it silently drifts from reality as cars are
  // added, sold, or moved between branches. Compute the real occupied count
  // directly from the cars collection instead of trusting the stored field.
  static async getOccupiedCapacityByBranch(): Promise<Record<string, number>> {
    try {
      const { ProductService } = await import('../car/carService');
      const allCars = await ProductService.getProducts();
      const counts: Record<string, number> = {};
      for (const car of allCars) {
        if (!car.branchId || !car.status || !this.OCCUPYING_STATUSES.has(car.status)) continue;
        counts[car.branchId] = (counts[car.branchId] || 0) + 1;
      }
      return counts;
    } catch (error) {
      console.error('Error computing occupied capacity by branch:', error);
      throw error;
    }
  }

  // Overrides each warehouse's stored (stale) capacity.used/available with the
  // real, computed-from-inventory values.
  static async withRealCapacity(warehouses: Warehouse[]): Promise<Warehouse[]> {
    const occupied = await this.getOccupiedCapacityByBranch();
    return warehouses.map(w => {
      const used = occupied[w.id || ''] || 0;
      const total = w.capacity?.total || 0;
      return {
        ...w,
        capacity: { total, used, available: Math.max(total - used, 0) },
      };
    });
  }

  private static mapDocToWarehouse(doc: QueryDocumentSnapshot<DocumentData>): Warehouse {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name || '',
      code: data.code || '',
      description: data.description || '',
      address: data.address as WarehouseAddress || { street: '', city: '', state: '', country: '', zipCode: '' },
      contact: data.contact as WarehouseContact || { phone: '', email: '', manager: '' },
      capacity: data.capacity as WarehouseCapacity || { total: 0, used: 0, available: 0 },
      isActive: data.isActive !== false,
      operatingHours: normalizeOperatingHours(data.operatingHours),
      displayOnWebsite: data.displayOnWebsite === true,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      createdBy: data.createdBy,
      createdByName: data.createdByName,
      updatedBy: data.updatedBy,
      updatedByName: data.updatedByName,
    };
  }
}
